import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Tooltip } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import GrantContent from '../../components/GrantContent'
import KarteraAdminTemplate from '../../template/KarteraAdminTemplate'
import { formatCount, RamoMark } from '../customers/common/customer-common'
import { vigenciaView } from '../policies/common/policy-common'
import PolicyDrawer from '../policies/drawer/policy-drawer'
import APIInvoke from '../../utils/APIInvoke'
import * as Permisos from '../../utils/Permisos'
import CommunicationDrawer from '../renewals/CommunicationDrawer'
import PaymentDrawer from './PaymentDrawer'
import {
    AGING,
    DUE_RANGES,
    daysFromToday,
    dueDaysLabel,
    dueRangeOf,
    money,
    moneyCompact,
    personName,
    shiftIso,
} from './collectionFormat'

const actionClass = 'inline-flex h-10 w-10 items-center justify-center rounded-xl border border-k-line-strong bg-k-surface text-k-ink hover:bg-k-hover'

const Collections = () => {
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState(null)
    const [recibos, setRecibos] = useState([])
    const [balances, setBalances] = useState([])
    const [totalItems, setTotalItems] = useState(0)
    const [reloadToken, setReloadToken] = useState(0)
    const [rangeId, setRangeId] = useState(DUE_RANGES[0].id)
    const [payRecibo, setPayRecibo] = useState(null)
    const [detail, setDetail] = useState(null)
    const [remind, setRemind] = useState(null)
    const [toast, setToast] = useState(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const range = DUE_RANGES.find((item) => item.id === rangeId) || DUE_RANGES[0]
                const start = shiftIso(range.from)
                const end = shiftIso(range.to)
                const response = await APIInvoke.invokeGET(`/recibos?fecVigDe[bw]=${start},${end}&status=pendiente&size=200&order=fecVigDe asc`)
                const payload = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || 'No se pudo cargar la cobranza.')
                const items = (payload.body?.items || []).map((recibo) => ({
                    ...recibo,
                    days: daysFromToday(recibo.fecVigDe),
                }))
                setRecibos(items)
                setTotalItems(payload.body?.totalItems || items.length)
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(loadError.message || 'No se pudo cargar la cobranza.')
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [reloadToken, rangeId])

    useEffect(() => {
        let active = true
        const loadBalances = async () => {
            try {
                const todos = DUE_RANGES.find((item) => item.id === 'todos') || DUE_RANGES[0]
                const response = await APIInvoke.invokeGET(`/recibos?fecVigDe[bw]=${shiftIso(todos.from)},${shiftIso(todos.to)}&status=pendiente&size=200&order=fecVigDe asc`)
                const payload = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || 'No se pudo cargar la antigüedad.')
                setBalances((payload.body?.items || []).map((recibo) => ({
                    ...recibo,
                    days: daysFromToday(recibo.fecVigDe),
                })))
            } catch {
                if (active) setBalances([])
            }
        }
        loadBalances()
        return () => { active = false }
    }, [reloadToken])

    useEffect(() => {
        if (!toast) return undefined
        const timer = window.setTimeout(() => setToast(null), 5000)
        return () => window.clearTimeout(timer)
    }, [toast])

    const selectRange = (id) => {
        setRangeId(id)
        setReloadToken((token) => token + 1)
    }

    const aging = useMemo(() => AGING.map((bucket) => {
        const items = balances.filter((recibo) => recibo.days != null && bucket.test(recibo.days))
        const amount = items.reduce((sum, recibo) => sum + Number(recibo.total || 0), 0)
        return { ...bucket, items, amount }
    }), [balances])

    const maxAmount = Math.max(...aging.map((bucket) => bucket.amount), 0)
    const currencies = new Set(recibos.map((recibo) => recibo.poliza?.moneda?.abreviacion).filter(Boolean))
    const totalAmount = recibos.reduce((sum, recibo) => sum + Number(recibo.total || 0), 0)
    const undoPayment = async () => {
        const reciboId = toast?.undoId
        setToast(null)
        if (!reciboId) return
        try {
            const response = await APIInvoke.invokePUT(`/recibos/${reciboId}/cancelarPago`)
            const payload = await response.json()
            if (!payload?.ok) throw new Error(payload?.message || 'No se pudo deshacer el pago.')
            setReloadToken((token) => token + 1)
            setToast({ message: 'Se canceló el pago y el recibo volvió a pendiente.' })
        } catch (undoError) {
            setToast({ message: undoError.message || 'No se pudo deshacer el pago.' })
        }
    }

    return (
        <KarteraAdminTemplate loading={false} padded={false} title="Cobranza">
            <PolicyDrawer polizaId={detail?.polizaId ?? null} initialTab={detail?.tab || 'general'} onClose={() => setDetail(null)} backUrl="/cxc" />
            <PaymentDrawer
                recibo={payRecibo}
                onClose={() => setPayRecibo(null)}
                onPaid={(reciboId, message) => {
                    setPayRecibo(null)
                    setRecibos((current) => current.filter((item) => item.id !== reciboId))
                    setTotalItems((count) => Math.max(count - 1, 0))
                    setToast({ message, undoId: reciboId })
                }}
            />
            <CommunicationDrawer
                entidad="recibo"
                target={remind}
                onClose={() => setRemind(null)}
                onDone={(message) => {
                    setRemind(null)
                    setToast({ message })
                }}
            />

            <div className="flex min-h-0 flex-1 flex-col gap-5 bg-k-bg px-[clamp(16px,2.5vw,32px)] pb-10 pt-6">
                {loading && !initialLoadDone && (
                    <div className="flex flex-col gap-5">
                        <div className="h-8 w-48 animate-pulse rounded bg-k-line" />
                        <div className="h-40 animate-pulse rounded-[18px] bg-k-line" />
                        <div className="h-80 animate-pulse rounded-[18px] bg-k-line" />
                    </div>
                )}

                {error && !loading && (
                    <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                        <div className="flex items-center">
                            <ErrorOutlineIcon className="mr-2 text-k-danger" style={{ fontSize: 20 }} />
                            <p className="m-0 text-sm text-k-danger-ink">{error}</p>
                        </div>
                        <button type="button" className="mt-3 h-10 rounded-xl bg-k-danger px-4 text-sm font-semibold text-white" onClick={() => setReloadToken((token) => token + 1)}>
                            Reintentar
                        </button>
                    </div>
                )}

                {initialLoadDone && !error && (
                    <>
                        <header className="flex flex-wrap items-end justify-between gap-4">
                            <div className="flex flex-col gap-1">
                                <span className="text-[13px] font-medium text-k-muted">
                                    {formatCount(recibos.length)} recibos pendientes
                                    {currencies.size === 1 ? ` · ${moneyCompact(totalAmount)}` : ''}
                                </span>
                                <h1 className="m-0 text-[28px] font-extrabold tracking-[-0.03em] text-k-ink">Cobranza</h1>
                            </div>
                            <div className="flex flex-wrap gap-2.5">
                                <button
                                    type="button"
                                    aria-label="Actualizar"
                                    title="Actualizar"
                                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-k-line bg-k-surface text-k-ink hover:bg-k-hover"
                                    onClick={() => setReloadToken((token) => token + 1)}
                                >
                                    <RefreshIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                                </button>
                            </div>
                        </header>

                        <section className="flex flex-col gap-[18px] rounded-[18px] border border-k-line bg-k-surface p-[22px]">
                            <div className="flex flex-wrap items-baseline justify-between gap-3">
                                <h2 className="m-0 text-[17px] font-semibold text-k-ink">Antigüedad de saldos</h2>
                                <span className="text-[13px] text-k-muted">Monto pendiente de −60 a 60 días</span>
                            </div>
                            <div className="flex h-[18px] gap-0.5 overflow-hidden rounded-[9px]" role="img" aria-label="Distribución del saldo pendiente por filtro">
                                {aging.map((bucket) => (
                                    <button
                                        key={bucket.id}
                                        type="button"
                                        title={bucket.label}
                                        aria-label={bucket.label}
                                        className="h-full border-0 p-0"
                                        style={{ flex: `${maxAmount ? bucket.amount : 1} 1 0`, background: bucket.color, minWidth: bucket.amount ? 4 : 0 }}
                                        onClick={() => selectRange(bucket.id)}
                                    />
                                ))}
                            </div>
                            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4">
                                {aging.map((bucket) => {
                                    const on = rangeId === bucket.id
                                    return (
                                        <button
                                            key={bucket.id}
                                            type="button"
                                            aria-pressed={on}
                                            className={`flex flex-col gap-1.5 rounded-2xl border bg-transparent p-3 text-left ${on ? 'border-k-ink' : 'border-transparent hover:bg-k-hover'}`}
                                            onClick={() => selectRange(bucket.id)}
                                        >
                                            <span className="flex items-center gap-2 text-[13px] text-k-muted">
                                                <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: bucket.color }} />
                                                {bucket.label}
                                            </span>
                                            <span className="text-[22px] font-medium tabular-nums text-k-ink">{moneyCompact(bucket.amount)}</span>
                                            <span className="text-xs text-k-muted">{formatCount(bucket.items.length)} recibos</span>
                                        </button>
                                    )
                                })}
                            </div>
                        </section>

                        <section className={`flex flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface ${loading ? 'opacity-60' : ''}`}>
                            <div className="flex flex-wrap justify-start gap-2 border-b border-k-line px-[18px] py-4" role="group" aria-label="Días por vencer">
                                {DUE_RANGES.map((filter) => {
                                    const on = rangeId === filter.id
                                    const label = filter.label || `${filter.from} a ${filter.to} días`
                                    const tone = filter.neutral
                                        ? (on ? 'border-k-ink bg-k-ink text-k-surface' : 'border-k-line-strong bg-k-surface text-k-ink hover:bg-k-hover')
                                        : (on ? '' : 'bg-k-surface text-k-ink hover:bg-k-hover')
                                    return (
                                        <button
                                            key={filter.id}
                                            type="button"
                                            aria-pressed={on}
                                            className={`inline-flex min-h-10 items-center gap-2 rounded-full border px-3.5 text-sm font-semibold ${tone}`}
                                            style={filter.neutral ? undefined : {
                                                background: on ? filter.color : undefined,
                                                borderColor: filter.color,
                                                color: on ? (filter.light ? '#1B1D29' : '#fff') : undefined,
                                            }}
                                            onClick={() => selectRange(filter.id)}
                                        >
                                            <span
                                                className={`h-2.5 w-2.5 rounded-full ${filter.neutral ? (on ? 'bg-k-surface' : 'bg-k-ink') : ''}`}
                                                style={filter.neutral ? undefined : { background: on ? (filter.light ? '#1B1D29' : '#fff') : filter.color }}
                                            />
                                            {label}
                                        </button>
                                    )
                                })}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[1000px] border-collapse text-sm">
                                    <thead>
                                        <tr className="bg-k-sunken text-left text-xs font-semibold uppercase tracking-[0.04em] text-k-muted">
                                            <th className="py-3 pl-[18px] pr-2.5">Recibo</th>
                                            <th className="px-2.5 py-3">Cliente</th>
                                            <th className="px-2.5 py-3">Aseguradora</th>
                                            <th className="px-2.5 py-3">Días por vencer</th>
                                            <th className="px-2.5 py-3">Vigencia</th>
                                            <th className="px-2.5 py-3 text-right">Importe</th>
                                            <th className="py-3 pl-2.5 pr-[18px] text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recibos.length === 0 && (
                                            <tr>
                                                <td colSpan={7} className="px-4 py-12 text-center text-sm text-k-muted">Sin recibos pendientes en este filtro.</td>
                                            </tr>
                                        )}
                                        {recibos.map((recibo) => {
                                            const poliza = recibo.poliza || {}
                                            const name = personName(poliza.cliente) || '—'
                                            const due = dueRangeOf(recibo.days)
                                            const vigencia = vigenciaView(recibo.fecVigDe, recibo.fecVigA, poliza.status)
                                            const polizaId = poliza.id ?? poliza.polizaId ?? null
                                            const rowSelected = detail?.reciboId === recibo.id
                                            return (
                                                <tr
                                                    key={recibo.id}
                                                    className={`cursor-pointer border-t border-k-line-soft transition-colors ${rowSelected ? 'bg-k-accent-soft' : 'hover:bg-k-hover'}`}
                                                    onClick={() => { if (polizaId != null) setDetail({ polizaId, reciboId: recibo.id }) }}
                                                >
                                                    <td className="py-2.5 pl-[18px] pr-2.5">
                                                        <div className="flex items-center gap-3">
                                                            <RamoMark ramo={poliza.ramo} boxed />
                                                            <div className="flex flex-col">
                                                                <span className="text-[13px] font-medium tabular-nums text-k-ink">{poliza.noPoliza || '—'}</span>
                                                                <span className="text-xs text-k-muted">Recibo {recibo.serie}/{recibo.totalSerie}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-2.5 py-3 font-semibold text-k-ink">{name}</td>
                                                    <td className="px-2.5 py-3">
                                                        <span className="flex min-w-0 flex-col">
                                                            <span className="truncate text-k-ink-2">{poliza.agenteClave?.aseguradora?.aseguradora || poliza.agenteClave?.aseguradora?.abreviacion || '—'}</span>
                                                            <span className="truncate text-xs tabular-nums text-k-muted">{poliza.agenteClave?.clave || ''}</span>
                                                        </span>
                                                    </td>
                                                    <td className="px-2.5 py-3">
                                                        <span
                                                            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-[9px] py-[3px] text-xs font-semibold ${due ? '' : 'bg-k-line text-k-ink-2'}`}
                                                            style={due ? { background: due.color, color: due.light ? '#1B1D29' : '#fff' } : undefined}
                                                        >
                                                            {due && <span className="h-1.5 w-1.5 rounded-full" style={{ background: due.light ? '#1B1D29' : '#fff' }} />}
                                                            {dueDaysLabel(recibo.days)}
                                                        </span>
                                                    </td>
                                                    <td className="px-2.5 py-3">
                                                        <span className="flex min-w-[150px] flex-col gap-1">
                                                            <span className="whitespace-nowrap text-[13px] text-k-ink-2">{vigencia.label}</span>
                                                            <span className="h-1 overflow-hidden rounded-sm bg-k-line" role="img" aria-label={`Avance de vigencia ${vigencia.progress}%`}>
                                                                <span className={`block h-full rounded-sm ${vigencia.barClass}`} style={{ width: `${vigencia.progress}%` }} />
                                                            </span>
                                                            {vigencia.note && <span className={`text-[11px] font-bold ${vigencia.noteClass}`}>{vigencia.note}</span>}
                                                        </span>
                                                    </td>
                                                    <td className="px-2.5 py-3 text-right text-[13px] tabular-nums text-k-ink">{money(recibo.total)}</td>
                                                    <td className="py-2 pl-2.5 pr-[18px]">
                                                        <div className="flex justify-end gap-1.5">
                                                            <Tooltip title="Enviar recordatorio" arrow>
                                                                <button
                                                                    type="button"
                                                                    aria-label={`Enviar recordatorio de ${poliza.noPoliza || 'la póliza'}`}
                                                                    className={actionClass}
                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation()
                                                                        setRemind({ recibo })
                                                                    }}
                                                                >
                                                                    <EmailOutlinedIcon style={{ fontSize: 16 }} />
                                                                </button>
                                                            </Tooltip>
                                                            <Tooltip title="Adjuntos" arrow>
                                                                <button
                                                                    type="button"
                                                                    aria-label={`Ver adjuntos de ${poliza.noPoliza || 'la póliza'}`}
                                                                    className={actionClass}
                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation()
                                                                        if (polizaId != null) setDetail({ polizaId, reciboId: recibo.id, tab: 'adjuntos' })
                                                                    }}
                                                                >
                                                                    <AttachFileOutlinedIcon style={{ fontSize: 16 }} />
                                                                </button>
                                                            </Tooltip>
                                                            <GrantContent permisos={[Permisos.POLIZAS_W]}>
                                                                <Tooltip title="Registrar pago" arrow>
                                                                    <button
                                                                        type="button"
                                                                        aria-label={`Registrar pago del recibo ${recibo.serie}/${recibo.totalSerie} de ${poliza.noPoliza || 'la póliza'}`}
                                                                        className={actionClass}
                                                                        onMouseDown={(event) => event.stopPropagation()}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation()
                                                                            setPayRecibo(recibo)
                                                                        }}
                                                                    >
                                                                        <PaymentsOutlinedIcon style={{ fontSize: 16 }} />
                                                                    </button>
                                                                </Tooltip>
                                                            </GrantContent>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {totalItems > recibos.length && (
                                <p className="m-0 border-t border-k-line px-[18px] py-3 text-[13px] text-k-muted">
                                    Se muestran {formatCount(recibos.length)} de {formatCount(totalItems)} recibos de la ventana.
                                </p>
                            )}
                        </section>
                    </>
                )}
            </div>

            {toast && (
                <div role="status" className="fixed bottom-6 right-6 z-[60] flex max-w-[calc(100%-48px)] items-center gap-3 rounded-xl bg-[#1B1D29] px-3 py-3 pl-4 text-sm text-white shadow-[0_12px_32px_rgba(27,29,41,0.3)]">
                    <span>{toast.message}</span>
                    {toast.undoId && (
                        <button type="button" className="h-9 rounded-lg px-2.5 text-sm font-semibold text-[#3FD3C6]" onClick={undoPayment}>Deshacer</button>
                    )}
                    <button type="button" aria-label="Cerrar aviso" className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-white" onClick={() => setToast(null)}>
                        <CloseIcon style={{ fontSize: 16 }} />
                    </button>
                </div>
            )}
        </KarteraAdminTemplate>
    )
}

export default Collections
